import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause } from "lucide-react";
import api, { API } from "@/lib/api";

export default function EmbedPlayer() {
  const audioRef = useRef(null);
  const [np, setNp] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [err, setErr] = useState("");

  // Read channel from URL ?ch= param so embedded players can be channel-specific
  const channel = (() => {
    if (typeof window === "undefined") return "all";
    try { return (new URLSearchParams(window.location.search).get("ch") || "all").toLowerCase(); }
    catch (e) { return "all"; }
  })();
  const channelLabel = channel === "all" ? "Mixxflow" : channel.charAt(0).toUpperCase() + channel.slice(1);

  const fetchNP = useCallback(async () => {
    try {
      const { data } = await api.get(`/now-playing?channel=${encodeURIComponent(channel)}`);
      setNp(data);
      return data;
    } catch (e) { return null; }
  }, [channel]);

  useEffect(() => {
    fetchNP();
    const id = setInterval(fetchNP, 10000);
    return () => clearInterval(id);
  }, [fetchNP]);

  const loadAndPlay = (data) => {
    if (!data?.playing || !data?.current) return;
    const audio = audioRef.current;
    const src = data.current.audio_url?.startsWith("http")
      ? data.current.audio_url
      : `${API}/mixes/${data.current.id}/audio`;
    audio.src = src;
    audio.load();
    const onReady = () => {
      try { audio.currentTime = data.offset || 0; } catch (e) { /* ignore */ }
      audio.play().then(() => setIsPlaying(true)).catch(() => setErr("Tap play"));
      audio.removeEventListener("loadedmetadata", onReady);
    };
    audio.addEventListener("loadedmetadata", onReady);
  };

  const toggle = async () => {
    if (isPlaying) { audioRef.current?.pause(); setIsPlaying(false); return; }
    const data = await fetchNP();
    if (data?.playing) loadAndPlay(data);
  };

  const onEnded = async () => {
    const data = await fetchNP();
    if (data?.playing) loadAndPlay(data);
  };

  const current = np?.current;
  const stationOrigin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="min-h-screen flex items-center justify-center p-3" data-testid="embed-player">
      <div className="player-card w-full max-w-[440px] p-4 flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          disabled={!current}
          className="play-btn !w-14 !h-14 flex-shrink-0"
          data-testid="embed-play-button"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" style={{ marginLeft: 2 }} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="live-dot" style={{ width: 6, height: 6 }}></span>
            <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#ff5566]">
              Live · {channelLabel}
            </span>
          </div>
          {current ? (
            <>
              <p className="text-sm font-semibold text-white truncate" data-testid="embed-title">{current.title}</p>
              <p className="text-[11px] text-[var(--text-muted)] font-mono truncate">{current.artist}</p>
            </>
          ) : (
            <p className="text-xs text-[var(--text-muted)] font-mono">{np?.message || "Tuning in..."}</p>
          )}
          {err && <p className="text-[10px] text-amber-400 font-mono mt-0.5">{err}</p>}
        </div>

        <a
          href={stationOrigin}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-mono uppercase tracking-widest text-[var(--brand-purple-light)] hover:text-[var(--neon-cyan)] flex-shrink-0"
          data-testid="embed-brand-link"
        >
          JAVI G ↗
        </a>

        <audio ref={audioRef} onEnded={onEnded} preload="none" />
      </div>
    </div>
  );
}
